import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDriverPushNotifications } from '../hooks/useDriverPushNotifications';
import { Bell, BellOff, Check, Package, Settings, X, Loader2, Volume2, VolumeX } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import axios from 'axios';
import { playDriverNotification, areSoundsEnabled, toggleSounds, initializeAudio } from '../utils/notificationSounds';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getToken = () => localStorage.getItem('halubri_driver_token');

const api = () => axios.create({
  baseURL: API,
  headers: { Authorization: `Bearer ${getToken()}` }
});

const DriverNotificationBell = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [soundsEnabled, setSoundsEnabled] = useState(areSoundsEnabled());
  const previousUnreadRef = useRef(0);
  const dropdownRef = useRef(null);
  
  const {
    isSupported,
    isSubscribed,
    permission,
    loading: pushLoading,
    toggle: togglePush
  } = useDriverPushNotifications();


  // Initialize audio on first interaction
  useEffect(() => {
    const handleFirstInteraction = () => {
      initializeAudio();
      document.removeEventListener('click', handleFirstInteraction);
    };
    document.addEventListener('click', handleFirstInteraction);
    return () => document.removeEventListener('click', handleFirstInteraction);
  }, []);

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    if (!getToken()) return;
    try {
      const [notifRes, countRes] = await Promise.all([
        api().get('/driver/notifications'),
        api().get('/driver/notifications/unread-count')
      ]);
      setNotifications(notifRes.data);
      const newUnreadCount = countRes.data.unread_count;
      
      // Play sound if new notifications arrived
      if (newUnreadCount > previousUnreadRef.current && soundsEnabled) {
        playDriverNotification();
      }
      
      previousUnreadRef.current = newUnreadCount;
      setUnreadCount(newUnreadCount);
    } catch (err) {
      console.error('Error fetching notifications:', err);
    }
  }, [soundsEnabled]);

  // Handle sound toggle
  const handleToggleSounds = () => {
    const newState = !soundsEnabled;
    setSoundsEnabled(newState);
    toggleSounds(newState);
    if (newState) {
      // Play a test sound when enabling
      playDriverNotification();
    }
  };

  // Poll for notifications
  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
        setShowSettings(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      await api().post('/driver/notifications/mark-read');
      setUnreadCount(0);
      setNotifications(notifications.map(n => ({ ...n, is_read: true })));
    } catch (err) {
      console.error('Error marking notifications as read:', err);
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Agora';
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    return `${diffDays}d`;
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="relative p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
        data-testid="driver-notification-bell"
      >
        <Bell className="w-5 h-5 text-white" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {showDropdown && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl overflow-hidden z-50">
          {/* Header */}
          <div className="px-4 py-3 bg-zinc-800 border-b border-zinc-700 flex items-center justify-between">
            <h3 className="font-barlow font-bold text-white">Notificações</h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="text-xs text-green-400 hover:text-green-300 flex items-center gap-1"
                >
                  <Check className="w-3 h-3" />
                  Marcar lidas
                </button>
              )}
              <button
                onClick={() => setShowSettings(!showSettings)}
                className="p-1 rounded hover:bg-zinc-700 text-zinc-400 hover:text-white"
              >
                <Settings className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Settings Panel */}
          {showSettings ? (
            <div className="p-4 space-y-4">
              <h4 className="font-medium text-white text-sm">Configurações</h4>
              
              {/* Sound Settings */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white text-sm">Som de Notificação</p>
                  <p className="text-zinc-400 text-xs">
                    Toque sonoro para novos pedidos
                  </p>
                </div>
                <Button
                  size="sm"
                  variant={soundsEnabled ? "outline" : "default"}
                  onClick={handleToggleSounds}
                  className={soundsEnabled ? "border-green-500 text-green-400 hover:bg-green-500/10" : "bg-zinc-600 hover:bg-zinc-700"}
                >
                  {soundsEnabled ? (
                    <>
                      <Volume2 className="w-4 h-4 mr-1" />
                      Ligado
                    </>
                  ) : (
                    <>
                      <VolumeX className="w-4 h-4 mr-1" />
                      Desligado
                    </>
                  )}
                </Button>
              </div>
              
              {!isSupported ? (
                <p className="text-xs text-zinc-400">
                  Notificações push não suportadas neste navegador.
                </p>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white text-sm">Notificações Push</p>
                      <p className="text-zinc-400 text-xs">
                        Receba alertas de novos pedidos
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant={isSubscribed ? "outline" : "default"}
                      onClick={togglePush}
                      disabled={pushLoading}
                      className={isSubscribed ? "border-red-500 text-red-400 hover:bg-red-500/10" : "bg-green-600 hover:bg-green-700"}
                    >
                      {pushLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : isSubscribed ? (
                        <>
                          <BellOff className="w-4 h-4 mr-1" />
                          Desactivar
                        </>
                      ) : (
                        <>
                          <Bell className="w-4 h-4 mr-1" />
                          Activar
                        </>
                      )}
                    </Button>
                  </div>
                  
                  {permission === 'denied' && (
                    <div className="bg-red-900/30 border border-red-700 rounded-lg p-2">
                      <p className="text-red-400 text-xs">
                        Permissão negada. Active nas configurações do navegador.
                      </p>
                    </div>
                  )}
                  
                  {isSubscribed && (
                    <div className="bg-green-900/30 border border-green-700 rounded-lg p-2">
                      <p className="text-green-400 text-xs flex items-center gap-1">
                        <Check className="w-3 h-3" />
                        Notificações push activas
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            /* Notifications List */
            <div className="max-h-80 overflow-y-auto">
              {notifications.length === 0 ? (
                <div className="p-8 text-center">
                  <Bell className="w-10 h-10 text-zinc-600 mx-auto mb-3" />
                  <p className="text-zinc-400 text-sm">Sem notificações</p>
                  <p className="text-zinc-500 text-xs mt-1">
                    Receberá alertas quando houver novos pedidos
                  </p>
                </div>
              ) : (
                notifications.map((notif) => (
                  <div
                    key={notif.id}
                    className={`px-4 py-3 border-b border-zinc-800 hover:bg-zinc-800/50 transition-colors cursor-pointer ${
                      !notif.is_read ? 'bg-green-900/20' : ''
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg ${
                        notif.notification_type === 'new_order' 
                          ? 'bg-green-600/20' 
                          : 'bg-blue-600/20'
                      }`}>
                        <Package className={`w-4 h-4 ${
                          notif.notification_type === 'new_order' 
                            ? 'text-green-400' 
                            : 'text-blue-400'
                        }`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-medium truncate">
                          {notif.title}
                        </p>
                        <p className="text-zinc-400 text-xs mt-0.5 line-clamp-2">
                          {notif.message}
                        </p>
                        <p className="text-zinc-500 text-xs mt-1">
                          {formatTime(notif.created_at)}
                        </p>
                      </div>
                      {!notif.is_read && (
                        <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DriverNotificationBell;
